import {
  getDefaultInstitutionalProfileFormData,
  type InstitutionalProfileFormData,
} from '@/types/institutionalProfile'
import { WRITE_ONLY_FIELDS } from './institutionalFields'

const STORAGE_KEY = 'institutional_signup_draft'

/**
 * A browser-local mirror of step 1, used **only before the organization exists**.
 *
 * The server-side draft is the real one, but it has nowhere to live until an
 * organization row exists, and an organization cannot be created without a name.
 * That leaves a window — "user typed twelve fields but has not yet typed the
 * institution name" — in which a reload would lose everything. This closes it.
 *
 * 🔴 The moment the organization exists this mirror is deleted and the server is
 * the only source of truth. Keeping both would mean deciding which wins on every
 * resume, and that decision is exactly how stale form data gets resurrected over
 * something the user has since corrected.
 *
 * 🔴 The write-only banking fields are never mirrored. Persisting a bank account
 * number to localStorage — where any script on the origin can read it, and where
 * it survives logout — would undo the point of encrypting it server-side.
 */
export const readLocalDraft = (): Partial<InstitutionalProfileFormData> | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

    // Only keys that actually belong to the form; a tampered or stale blob must
    // not be able to inject arbitrary properties into form state.
    const defaults = getDefaultInstitutionalProfileFormData()
    const result: Partial<InstitutionalProfileFormData> = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!(key in defaults)) continue
      if (WRITE_ONLY_FIELDS.includes(key as keyof InstitutionalProfileFormData)) continue
      const expected = typeof defaults[key as keyof InstitutionalProfileFormData]
      if (typeof value !== expected) continue
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(result as any)[key] = value
    }
    return result
  } catch {
    return null
  }
}

export const writeLocalDraft = (profile: InstitutionalProfileFormData): void => {
  try {
    const safe: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(profile)) {
      if (WRITE_ONLY_FIELDS.includes(key as keyof InstitutionalProfileFormData)) continue
      safe[key] = value
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe))
  } catch {
    // Quota exceeded or storage disabled (Safari private mode). Losing the local
    // mirror is a downgrade, not a failure — the server draft still works.
  }
}

export const clearLocalDraft = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing to do */
  }
}
